import { tt } from '@agos/shared'

/**
 * The addresses this customer can deliver to.
 *
 * `GET /customer-auth/me/saved-addresses` builds the list from two sources and the client sees only the
 * result: the addresses they saved by hand, then the ones taken from their past orders — and only from
 * channels that actually deliver to one (`sales_channels.requires_delivery_address`). Hidden addresses
 * are already filtered out server-side, and duplicates are already collapsed.
 *
 * `manuallyAdded` is the difference between the two, and it is the one thing the screen must not blur:
 * removing a saved address is *deleting something the customer typed*, while removing one drawn from
 * their history is *asking not to be offered it again*. Both go through
 * `POST /hidden-addresses`, so the wording is the only place the distinction survives.
 */

export interface SavedAddress {
  address: string
  label: string | null
  isDefault: boolean
  manuallyAdded: boolean
}

export function addressesOf(payload: unknown): SavedAddress[] {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const rows = Array.isArray(row.addresses) ? row.addresses : []
  return rows.flatMap((entry) => {
    const item = entry as Record<string, unknown>
    const address = typeof item.address === 'string' ? item.address.trim() : ''
    if (address === '') return []
    return [
      {
        address,
        label: typeof item.label === 'string' && item.label.trim() !== '' ? item.label : null,
        isDefault: item.isDefault === true,
        manuallyAdded: item.manuallyAdded === true,
      },
    ]
  })
}

/** What the button says, which is not the same promise in both cases. */
export function removeLabel(address: SavedAddress): string {
  return address.manuallyAdded ? tt('Odstrániť') : tt('Neponúkať')
}

export function removeExplanation(address: SavedAddress): string {
  return address.manuallyAdded
    ? tt('Adresu, ktorú ste pridali, odstránime zo zoznamu.')
    : tt('Adresu z vašich objednávok vám prestaneme ponúkať. V histórii objednávok zostáva.')
}

/**
 * Whether this label is worth sending.
 *
 * A blank label *clears* it — `label(...)` is `if (label == null || label.isBlank()) labels.remove(...)`
 * — so an empty box is a meaningful instruction rather than a no-op, and only an unchanged value is
 * worth skipping.
 */
export function labelChanged(address: SavedAddress, typed: string): boolean {
  const next = typed.trim()
  return next !== (address.label ?? '')
}

/**
 * The blank case sends `''` rather than `null`.
 *
 * The server treats the two identically, and the generated request type has no null — `label?: string`,
 * one of the nullable-request-field mismatches already recorded in `BLOCKED.md`. Sending the empty
 * string clears the label and needs no cast around the spec.
 */
export function labelPayload(address: SavedAddress, typed: string): { address: string; label: string } {
  return { address: address.address, label: typed.trim() }
}

/**
 * Making an address the default one - §9.332.
 *
 * **The owner:** *"I cant set address to Predvolená."* The badge was drawn and nothing set it: the
 * row showed *Predvolená* when `isDefault` was already true, and the profile had no action that
 * could make it true. `PATCH /customer-auth/me` has taken `defaultAddress` all along - legacy sets
 * it the same way (`MojProfil.tsx:760`) - and `CustomerAddressService` computes `isDefault` from it.
 *
 * <p>Clearing is the same call with the empty string, which is what `CustomerAccountService` reads as
 * *no default*: `text(changes.get("defaultAddress"))` then blank-to-null. So a customer can un-set
 * one, which matters because the default is what the checkout prefills.
 */
export function defaultAddressPayload(address: string | null): { defaultAddress: string } {
  return { defaultAddress: address === null ? '' : address.trim() }
}

/** Whether this row is worth offering the action on: making the default the default does nothing. */
export function maySetDefault(entry: SavedAddress): boolean {
  return entry.isDefault !== true && entry.address.trim() !== ''
}

/**
 * How to name an address in one line - §9.332.
 *
 * **The owner:** *"add dropdown for these addresses, so I can actually see that Address."* The
 * checkout's saved-address chips render `entry.label ?? entry.address`, so an address the customer
 * has named shows **only the name** - *Domov* with the street nowhere on the screen, which is
 * exactly not being able to see the address.
 *
 * Both, label first, because the label is how they recognise it and the address is what they are
 * checking. An unlabelled one is just the address rather than a separator with nothing before it.
 */
export function addressSummary(entry: SavedAddress): string {
  const label = entry.label === null ? '' : entry.label.trim()
  return label === '' ? entry.address : `${label} - ${entry.address}`
}

/** The one to prefill the checkout with, or `null` when the customer has not chosen. */
export function defaultAddress(addresses: readonly SavedAddress[]): SavedAddress | null {
  return addresses.find((entry) => entry.isDefault) ?? null
}

/**
 * Whether saving this address would add anything.
 *
 * The server collapses duplicates when it builds the list, so re-saving one already there is accepted
 * and changes nothing — which looks to the customer like the button not working.
 */
export function alreadyKnown(addresses: readonly SavedAddress[], typed: string): boolean {
  const next = typed.trim()
  return next !== '' && addresses.some((entry) => entry.address === next)
}
